/**
 * Filter + precedence helpers for the Approval Workspace queue
 * (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).
 *
 * Pure functions only. No Vue, no Pinia, no axios. Imported by the
 * Pinia store (for grouping + counts) and by the filter chip component
 * (for chip labels). Tested in isolation by
 * ``__tests__/filters.spec.js`` -- the precedence rule is the most
 * load-bearing piece here.
 *
 * Plan ref: implementation-plan/03-approval-workspace.md, P2-Q2.
 */

/**
 * Canonical status vocabulary mirroring ADR 0040 + the F1.a
 * APPROVAL_STATUS_STATES set. The order here is the user-facing
 * display order in the lifecycle stepper too.
 */
export const APPROVAL_STATUSES = Object.freeze([
    'awaiting_approval',
    'changes_requested',
    'rejected',
    'approved',
])

/**
 * User-visible status label. Matter-of-fact, specific. Used by the
 * status chip + lifecycle stepper + filter chips.
 */
export const STATUS_LABELS = Object.freeze({
    awaiting_approval: 'Awaiting approval',
    changes_requested: 'Changes requested',
    rejected: 'Rejected',
    approved: 'Approved',
})

/**
 * Stale-state vocabulary from F1.a (``approval_records.stale_state``).
 * The workspace highlights everything past 'active' as needing
 * triage.
 */
export const STALE_STATES = Object.freeze([
    'active',
    'reminded',
    'escalated',
    'expired',
])

/**
 * Stale-state user-visible labels for the StaleCallout banner.
 */
export const STALE_LABELS = Object.freeze({
    reminded: 'Reminder sent',
    escalated: 'Escalated to account owner',
    expired: 'Approval link expired',
})

/**
 * Filter chip definitions in display order. ``key`` matches the
 * Pinia store's ``filter`` value; ``label`` is what the chip renders.
 *
 * "Needs attention" is the default and combines the rejected,
 * changes-requested, to-send, and stale buckets via the precedence
 * rule below.
 */
export const FILTER_CHIPS = Object.freeze([
    { key: 'needs_attention', label: 'Needs attention' },
    { key: 'awaiting_approval', label: 'Awaiting approval' },
    { key: 'changes_requested', label: 'Changes requested' },
    { key: 'to_send', label: 'To send' },
    { key: 'approved', label: 'Approved' },
    { key: 'all', label: 'All' },
])

/**
 * P2-Q2 precedence rule. A queue entry is counted in exactly ONE
 * "Needs attention" bucket. The order below is the precedence:
 *
 *     rejected  >  changes_requested  >  to_send  >  stale
 *
 * Exported as a table so the unit test can iterate fixtures without
 * embedding the rule in two places.
 */
export const NEEDS_ATTENTION_PRECEDENCE = Object.freeze([
    'rejected',
    'changes_requested',
    'to_send',
    'stale',
])

/**
 * Classify one queue entry into a precedence bucket (or null if it
 * does not need attention). The queue entry is the flat ApprovalRecord
 * payload returned by GET /api/approval_records, enriched on the
 * client with ``artwork.pipeline_state``.
 *
 * @param {object} entry - { status, stale_state, artwork: { pipeline_state } }
 * @returns {string|null} bucket name from NEEDS_ATTENTION_PRECEDENCE,
 *                        or null when the entry is healthy.
 */
export function needsAttentionBucket(entry) {
    if (!entry) return null
    if (entry.status === 'rejected') return 'rejected'
    if (entry.status === 'changes_requested') return 'changes_requested'
    // ``to_send`` is the uploaded-but-not-yet-routed bucket; we
    // surface it via the synthetic ``artwork.pipeline_state==='uploaded'``
    // signal injected by the store when it hydrates artwork rows.
    const ps = entry.artwork && entry.artwork.pipeline_state
    if (ps === 'uploaded') return 'to_send'
    // Stale only counts on records still awaiting client action.
    if (
        entry.status === 'awaiting_approval' &&
        entry.stale_state &&
        entry.stale_state !== 'active'
    ) {
        return 'stale'
    }
    return null
}

/**
 * Does this entry belong in the "Needs attention" filter?
 */
export function isNeedsAttention(entry) {
    return needsAttentionBucket(entry) !== null
}

/**
 * Filter predicate for the queue's currently-selected chip. Returns
 * true when the entry matches; mirrors the design's chip semantics:
 *
 *  - ``needs_attention``: precedence rule above (any bucket).
 *  - ``to_send``: artwork is in pipeline_state 'uploaded'.
 *  - status keys: ApprovalRecord.status === key.
 *  - ``all``: pass-through.
 */
export function matchesFilter(entry, filterKey) {
    if (!entry || !filterKey) return false
    if (filterKey === 'all') return true
    if (filterKey === 'needs_attention') return isNeedsAttention(entry)
    if (filterKey === 'to_send') {
        const ps = entry.artwork && entry.artwork.pipeline_state
        return ps === 'uploaded'
    }
    return entry.status === filterKey
}

/**
 * Group a flat list of queue entries by their client_id, preserving
 * the input order of the first occurrence (so an "updated_at DESC"
 * server sort stays meaningful).
 *
 * Returns ``[{client_id, client, items: [...]}, ...]``.
 *
 * ``client`` is hydrated from the supplied ``clientsById`` lookup;
 * when the lookup is missing the client (race: queue arrived before
 * the client list), we fall back to a name string of the form
 * ``Client <id>`` so the UI still renders meaningfully.
 */
export function groupByClient(entries, clientsById = {}) {
    if (!Array.isArray(entries) || entries.length === 0) return []
    const order = []
    const buckets = new Map()
    for (const entry of entries) {
        const cid = entry?.client_id
        if (cid == null) continue
        if (!buckets.has(cid)) {
            order.push(cid)
            buckets.set(cid, [])
        }
        buckets.get(cid).push(entry)
    }
    return order.map((cid) => ({
        client_id: cid,
        client: clientsById[cid] || { id: cid, name: `Client ${cid}` },
        items: buckets.get(cid),
    }))
}

/**
 * Compute the counts shown on the summary cards + filter chips.
 *
 * Returns an object with one number per chip key plus ``stale_total``,
 * which the summary header uses to highlight the stale/escalated card.
 */
export function computeCounts(entries) {
    const safe = Array.isArray(entries) ? entries : []
    const counts = {
        needs_attention: 0,
        awaiting_approval: 0,
        changes_requested: 0,
        rejected: 0,
        to_send: 0,
        approved: 0,
        all: safe.length,
        stale_total: 0,
        // The summary header's "Needs builder action" card combines
        // rejected + changes-requested. We surface it explicitly so
        // the header can read a single key.
        needs_builder: 0,
    }
    for (const entry of safe) {
        if (isNeedsAttention(entry)) counts.needs_attention += 1
        if (entry.status === 'awaiting_approval') {
            counts.awaiting_approval += 1
        }
        if (entry.status === 'changes_requested') {
            counts.changes_requested += 1
            counts.needs_builder += 1
        }
        if (entry.status === 'rejected') {
            counts.rejected += 1
            counts.needs_builder += 1
        }
        if (entry.status === 'approved') counts.approved += 1
        const ps = entry.artwork && entry.artwork.pipeline_state
        if (ps === 'uploaded') counts.to_send += 1
        if (
            entry.status === 'awaiting_approval' &&
            entry.stale_state &&
            entry.stale_state !== 'active'
        ) {
            counts.stale_total += 1
        }
    }
    return counts
}
